import { type BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { createAxiosInstance } from '../../api'
import {
  CreateGrantRequest,
  CreateResearcherData,
  GrantData,
  GrantListPage,
  GrantUserData,
  OrganizationWorkData,
  SearchGrantsParams,
  UpdateGrantData,
  WorkData,
} from '../../types'

export const apiGrantBaseQuery =
  (): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }) => {
    const api = createAxiosInstance(`${String(process.env.REACT_APP_ENGINE_API_URL)}/reporting/grant`)

    try {
      const result = await api(url, {
        method,
        data,
        params,
      })
      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError
      console.error(err)

      throw axiosError
    }
  }

export const grantApi = createApi({
  reducerPath: 'grantApi',
  baseQuery: apiGrantBaseQuery(),
  endpoints: (builder) => ({
    importGrantFile: builder.mutation<string, FormData>({
      query(body) {
        return {
          url: '/import',
          method: 'post',
          data: body,
          formData: true,
        }
      },
    }),
    getGrants: builder.query<GrantData[], void>({
      query() {
        return {
          url: '',
          method: 'get',
        }
      },
    }),
    searchGrants: builder.mutation<GrantListPage, { data: SearchGrantsParams }>({
      query({ data }) {
        return {
          url: 'search',
          method: 'post',
          data,
        }
      },
    }),
    addGrant: builder.mutation<GrantData, { data: CreateGrantRequest }>({
      query({ data }) {
        return {
          url: '',
          method: 'post',
          data,
        }
      },
    }),
    updateGrant: builder.mutation<GrantData, { data: UpdateGrantData; grantId: string }>({
      query({ data, grantId }) {
        return {
          url: `/${grantId}`,
          method: 'put',
          data,
        }
      },
    }),
    getGrant: builder.query<GrantData, string>({
      query(grantId: string) {
        return {
          url: `/${grantId}`,
          method: 'get',
        }
      },
    }),
    getGrantUsers: builder.query<GrantUserData[], string>({
      query(grantId: string) {
        return {
          url: `/${grantId}/users`,
          method: 'get',
        }
      },
    }),
    addGrantUser: builder.mutation<GrantUserData, { data: CreateResearcherData; grantId: string }>({
      query({ data, grantId }) {
        return {
          url: `/${grantId}/users`,
          method: 'post',
          data,
        }
      },
    }),
    getGrantWorks: builder.query<OrganizationWorkData[], string>({
      query(grantId: string) {
        return {
          url: `/${grantId}/work-products`,
          method: 'get',
        }
      },
    }),
  }),
})

export const {
  useImportGrantFileMutation,
  useLazyGetGrantsQuery,
  useSearchGrantsMutation,
  useAddGrantMutation,
  useAddGrantUserMutation,
  useUpdateGrantMutation,
  useLazyGetGrantQuery,
  useLazyGetGrantUsersQuery,
  useLazyGetGrantWorksQuery,
} = grantApi
